// Shared lists, projects and areas (docs/43), as the kit's Sharing.swift and FirestoreBackend.swift handle them; the
// kit is the reference. A share is a container on the server, `shares/{id}`, holding the shared records in the same
// shapes as `users/{uid}`; only the server's `share` function decides who is in it.
//
// - Where a record lives (ShareRouting): a to-do in a shared project or list, or loose in a shared area; a section of
//   a shared project; a project that is shared, or in a shared area; a shared area. Everything else is the person's
//   own, under `users/{uid}`. A project that moves into or out of a share takes its to-dos and sections along.
// - What is the person's own in a share of someone else (SharePlacement, docs/47 rule 10): Today's section, the plan
//   and its order for a to-do; whether a section is collapsed; where a list or project shared on its own shows (its
//   area and order). These are read from `users/{uid}/placements/{shareID}` over the owner's values, and never written
//   into the share: they go to the placement.
// - Who may write (the Firestore rules): every member the to-dos; only the owner the projects, sections and areas.

import type { Area, CollectionName, Placement, Project, Section, Share, Todo, TodoPlan } from "./records";
import { PERSONAL_PROJECT_FIELDS, PERSONAL_SECTION_FIELDS, PERSONAL_TODO_FIELDS, isAreaShare } from "./records";

/** The collections a share holds; the others always live under `users/{uid}`. */
export type ShareableCollection = "todos" | "projects" | "areas" | "sections";

export function isShareable(collection: CollectionName): collection is ShareableCollection {
  return collection === "todos" || collection === "projects" || collection === "areas" || collection === "sections";
}

// MARK: - Where a record lives

/** Which share a record lives in, from what it is in (ShareRouting). Null: the person's own data. */
export class ShareRouting {
  private readonly shareOfProjectID = new Map<string, string>();
  private readonly shareOfAreaID = new Map<string, string>();
  private readonly projects = new Map<string, Project>();

  constructor(shares: readonly Share[], projects: readonly Project[]) {
    for (const share of shares) {
      if (isAreaShare(share)) this.shareOfAreaID.set(share.recordID, share.id);
      else this.shareOfProjectID.set(share.recordID, share.id);
    }
    // The first of the same id wins, as in the kit.
    for (const project of projects) if (!this.projects.has(project.id)) this.projects.set(project.id, project);
  }

  ofProject(project: Pick<Project, "id" | "areaID">): string | null {
    return this.shareOfProjectID.get(project.id) ?? (project.areaID === undefined ? undefined : this.shareOfAreaID.get(project.areaID)) ?? null;
  }

  ofArea(area: Pick<Area, "id">): string | null {
    return this.shareOfAreaID.get(area.id) ?? null;
  }

  ofSection(section: Pick<Section, "projectID">): string | null {
    const project = section.projectID === undefined ? undefined : this.projects.get(section.projectID);
    return project ? this.ofProject(project) : null;
  }

  ofTodo(todo: Pick<Todo, "projectID" | "areaID" | "habitID">): string | null {
    // A habit's days stay the person's own, also in a shared area (ShareRouting.share(of todo:); docs/63).
    if (todo.habitID !== undefined) return null;
    if (todo.projectID !== undefined) {
      const shared = this.shareOfProjectID.get(todo.projectID);
      if (shared !== undefined) return shared;
      const project = this.projects.get(todo.projectID);
      return project ? this.ofProject(project) : null;
    }
    return todo.areaID === undefined ? null : this.shareOfAreaID.get(todo.areaID) ?? null;
  }

  /** The share of a record of a collection that a share can hold. */
  of(collection: ShareableCollection, record: Todo | Project | Area | Section): string | null {
    switch (collection) {
      case "todos": return this.ofTodo(record as Todo);
      case "projects": return this.ofProject(record as Project);
      case "areas": return this.ofArea(record as Area);
      case "sections": return this.ofSection(record as Section);
    }
  }
}

/**
 * The records whose share differs between two routings (before and after a change), leaving out `except` (written
 * anyway): when a project moves into or out of a share, its to-dos and sections go with it although none of them
 * changed. Each is written whole at its new place, with a tombstone where it was (ShareRouting.rehomed).
 */
export function rehomed<T extends { id: string }>(
  records: readonly T[], except: ReadonlySet<string>, before: ShareRouting, after: ShareRouting, shareOf: (routing: ShareRouting, record: T) => string | null,
): T[] {
  return records.filter((record) => !except.has(record.id) && shareOf(before, record) !== shareOf(after, record));
}

/** The path of a record's document: `users/{uid}/{collection}/{id}`, or `shares/{shareID}/{collection}/{id}`. */
export function documentPath(uid: string, collection: CollectionName, id: string, shareID: string | null = null): string {
  return shareID === null ? `users/${uid}/${collection}/${id}` : `shares/${shareID}/${collection}/${id}`;
}

/** The path of the person's placement for a share. */
export function placementPath(uid: string, shareID: string): string {
  return `users/${uid}/placements/${shareID}`;
}

// MARK: - Who may do what

/** A share someone else owns, by its id: the one where the person's own values go to their placement. */
export function shareOfSomeoneElse(shareID: string | null, shares: readonly Share[], uid: string): Share | null {
  if (shareID === null) return null;
  const share = shares.find((candidate) => candidate.id === shareID);
  return share && share.ownerUID !== uid ? share : null;
}

/**
 * Whether the person may write a record of the collection where it lives (the Firestore rules): their own data
 * always; in a share they are in, the to-dos always and the rest only when they own it.
 */
export function canWrite(collection: ShareableCollection, shareID: string | null, shares: readonly Share[], uid: string): boolean {
  if (shareID === null) return true;
  const share = shares.find((candidate) => candidate.id === shareID);
  if (!share || !share.memberUIDs.includes(uid)) return false;
  return collection === "todos" || share.ownerUID === uid;
}

/**
 * Whether the person owns what a share holds: their own data counts as theirs, a share only when they made it. Only
 * the owner may delete, rename, lay out or reorder what is shared, or change a shared list's end (Store.isOwner,
 * canEdit); every member may change its to-dos.
 */
export function isOwner(shareID: string | null, shares: readonly Share[], uid: string): boolean {
  if (shareID === null) return true;
  const share = shares.find((candidate) => candidate.id === shareID);
  return share === undefined || share.ownerUID === uid;
}

/**
 * Whether the person may share a project or area (Store.canShare): their own, not the birthdays list, not inside
 * something shared already; an area not while it holds a project shared on its own (that would be two shares).
 */
export function canShare(
  target: { kind: "project"; project: Project } | { kind: "area"; area: Area }, shares: readonly Share[], projects: readonly Project[],
): boolean {
  const routing = new ShareRouting(shares, projects);
  if (target.kind === "project") return target.project.kind !== "birthdays" && routing.ofProject(target.project) === null;
  const id = target.area.id;
  return routing.ofArea(target.area) === null
    && !projects.some((project) => project.areaID === id && routing.ofProject(project) !== null);
}

/**
 * Whether a project may go into an area (Store.canMove): a project shared on its own may not go into a shared area,
 * which would put it into two shares. The apps grey such a destination, and the store refuses the move.
 */
export function canMove(project: Pick<Project, "id">, areaID: string, shares: readonly Share[], areas: readonly Area[], projects: readonly Project[]): boolean {
  const area = areas.find((candidate) => candidate.id === areaID);
  if (!area || new ShareRouting(shares, projects).ofArea(area) === null) return true;
  return !shares.some((share) => !isAreaShare(share) && share.recordID === project.id);
}

/** The name of whoever completed a to-do of a share, if it was someone in it (Store.completedByName). */
export function completedByName(todo: Todo, share: Share | null): string | undefined {
  if (todo.completedBy === undefined || !share) return undefined;
  return share.members[todo.completedBy]?.name;
}

// MARK: - The person's own values (SharePlacement)

/** The to-do with this person's plan in place of the owner's. */
export function placedTodo(todo: Todo, placement: Placement): Todo {
  const plan = placement.plans?.[todo.id];
  const placed: Todo = { ...todo };
  delete placed.todaySectionID;
  delete placed.plannedUntil;
  delete placed.planOrder;
  if (plan?.todaySectionID !== undefined) placed.todaySectionID = plan.todaySectionID;
  if (plan?.plannedUntil !== undefined) placed.plannedUntil = plan.plannedUntil;
  if (plan?.planOrder !== undefined) placed.planOrder = plan.planOrder;
  return placed;
}

/** The section collapsed as this person left it. */
export function placedSection(section: Section, placement: Placement): Section {
  const placed: Section = { ...section };
  if (placement.collapsed?.[section.id] === true) placed.isCollapsed = true;
  else delete placed.isCollapsed;
  return placed;
}

/** The shared list or project where this person placed it (its area, and its order when they moved it). */
export function placedProject(project: Project, placement: Placement): Project {
  const placed: Project = { ...project };
  if (placement.areaID === undefined) delete placed.areaID;
  else placed.areaID = placement.areaID;
  if (placement.order !== undefined) placed.order = placement.order;
  return placed;
}

/**
 * A record read from a share, with this person's own values in place (FirestoreBackend.placed): only in a share of
 * someone else; a project only when it is shared on its own (in a shared area, its area and order are the owner's).
 */
export function placedRecord<T extends Todo | Project | Section>(
  collection: "todos" | "projects" | "sections", record: T, share: Share, uid: string, placement: Placement | undefined,
): T {
  if (share.ownerUID === uid) return record;
  const own = placement ?? {};
  switch (collection) {
    case "todos": return placedTodo(record as Todo, own) as T;
    case "sections": return placedSection(record as Section, own) as T;
    case "projects": return isAreaShare(share) ? record : (placedProject(record as Project, own) as T);
  }
}

function samePlan(a: TodoPlan | undefined, b: TodoPlan | undefined): boolean {
  return a?.todaySectionID === b?.todaySectionID && a?.plannedUntil?.getTime() === b?.plannedUntil?.getTime() && a?.planOrder === b?.planOrder;
}

/** The placement remembering the to-do's plan, or null when it holds that already (SharePlacement.note). */
export function notedTodo(placement: Placement, todo: Todo): Placement | null {
  const plan: TodoPlan = {};
  if (todo.todaySectionID !== undefined) plan.todaySectionID = todo.todaySectionID;
  if (todo.plannedUntil !== undefined) plan.plannedUntil = todo.plannedUntil;
  if (todo.planOrder !== undefined) plan.planOrder = todo.planOrder;
  const known = placement.plans?.[todo.id];
  if (Object.keys(plan).length === 0) {
    if (known === undefined) return null;
    const plans = { ...placement.plans };
    delete plans[todo.id];
    return { ...placement, plans };
  }
  if (known !== undefined && samePlan(known, plan)) return null;
  return { ...placement, plans: { ...placement.plans, [todo.id]: plan } };
}

/** The placement remembering whether the section is collapsed, or null when it holds that already. */
export function notedSection(placement: Placement, section: Section): Placement | null {
  const known = placement.collapsed?.[section.id];
  if (section.isCollapsed === true) {
    if (known === true) return null;
    return { ...placement, collapsed: { ...placement.collapsed, [section.id]: true } };
  }
  if (known === undefined) return null;
  const collapsed = { ...placement.collapsed };
  delete collapsed[section.id];
  return { ...placement, collapsed };
}

/** The placement remembering where the project shows, or null when it holds that already. */
export function notedProject(placement: Placement, project: Project): Placement | null {
  if (placement.areaID === project.areaID && placement.order === project.order) return null;
  const noted: Placement = { ...placement, order: project.order };
  if (project.areaID === undefined) delete noted.areaID;
  else noted.areaID = project.areaID;
  return noted;
}

/** Where a saved record goes, and which of its fields stay out of the share. */
export interface PlacedSave<T> {
  record: T;
  /** Null: under `users/{uid}`. */
  shareID: string | null;
  /** Fields neither compared nor written (changes.ts: `SaveOptions.leaveOut`): the person's own, in their placement. */
  leaveOut: readonly string[];
}

/**
 * Where each saved record of a change is written, and the placements that change with it (FirestoreBackend.apply):
 * records in a share of someone else leave out the person's own fields, which go to their placement for that share.
 * `placements` are the person's placements as known, by share id; the result holds only those that changed.
 */
export function planSaves(
  saved: { todos?: readonly Todo[]; projects?: readonly Project[]; sections?: readonly Section[]; areas?: readonly Area[] },
  routing: ShareRouting, shares: readonly Share[], uid: string, placements: Readonly<Record<string, Placement>>,
): {
  todos: PlacedSave<Todo>[]; projects: PlacedSave<Project>[]; sections: PlacedSave<Section>[]; areas: PlacedSave<Area>[];
  placements: Record<string, Placement>;
} {
  const changed: Record<string, Placement> = {};
  const note = (shareID: string, change: (placement: Placement) => Placement | null) => {
    const next = change(changed[shareID] ?? placements[shareID] ?? {});
    if (next) changed[shareID] = next;
  };
  const todos = (saved.todos ?? []).map((record) => {
    const shareID = routing.ofTodo(record);
    const other = shareOfSomeoneElse(shareID, shares, uid);
    if (other) note(other.id, (placement) => notedTodo(placement, record));
    return { record, shareID, leaveOut: other ? PERSONAL_TODO_FIELDS : [] };
  });
  const projects = (saved.projects ?? []).map((record) => {
    const shareID = routing.ofProject(record);
    const other = shareOfSomeoneElse(shareID, shares, uid);
    const own = other !== null && !isAreaShare(other);
    if (own) note(other.id, (placement) => notedProject(placement, record));
    return { record, shareID, leaveOut: own ? PERSONAL_PROJECT_FIELDS : [] };
  });
  const sections = (saved.sections ?? []).map((record) => {
    const shareID = routing.ofSection(record);
    const other = shareOfSomeoneElse(shareID, shares, uid);
    if (other) note(other.id, (placement) => notedSection(placement, record));
    return { record, shareID, leaveOut: other ? PERSONAL_SECTION_FIELDS : [] };
  });
  const areas = (saved.areas ?? []).map((record) => ({ record, shareID: routing.ofArea(record), leaveOut: [] as readonly string[] }));
  return { todos, projects, sections, areas, placements: changed };
}
